import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  IconButton,
  Button,
  Tooltip,
  alpha,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  List as ListIcon,
  Bug,
  Lightbulb,
  Zap,
  HelpCircle,
  FileText,
  Repeat,
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { Dayjs } from 'dayjs';

import { taskService } from '@/services/api';
import { QUERY_KEYS } from '@/services/queryClient';
import { Task, TaskStatus, TaskPriority, IssueType } from '@/types';

// Issue type config
const issueTypeConfig: Record<IssueType, { icon: React.FC<any>; color: string; label: string }> = {
  bug: { icon: Bug, color: '#dc2626', label: 'Bug' },
  feature: { icon: Lightbulb, color: '#16a34a', label: 'Feature' },
  improvement: { icon: Zap, color: '#4F46E5', label: 'Improvement' },
  task: { icon: FileText, color: '#6b7280', label: 'Task' },
  question: { icon: HelpCircle, color: '#9333ea', label: 'Question' },
};

type ViewMode = 'month' | 'week';

const MONTH_CELL_MAX_ITEMS = 3;
const WEEK_VISIBLE_TASKS = 8;

interface UserCalendarViewProps {
  userId: string;
  userName: string;
}

/**
 * UserCalendarView
 * Calendar view for a specific user's tasks.
 * Includes every task the user is on (any RACI role) whose due date falls in the visible range.
 */
const UserCalendarView: React.FC<UserCalendarViewProps> = ({ userId, userName }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [currentDate, setCurrentDate] = useState(dayjs());
  const [viewMode, setViewMode] = useState<ViewMode>('month');
  const [showRecurring, setShowRecurring] = useState(true);
  const [dayList, setDayList] = useState<{ date: Dayjs; tasks: Task[] } | null>(null);

  const rangeStart = (viewMode === 'week' ? currentDate.startOf('week') : currentDate.startOf('month')).format('YYYY-MM-DD');
  const rangeEnd = (viewMode === 'week' ? currentDate.endOf('week') : currentDate.endOf('month')).format('YYYY-MM-DD');

  const { data: tasksResponse } = useQuery({
    queryKey: [...QUERY_KEYS.tasks, 'user', userId, rangeStart, rangeEnd],
    queryFn: async () => {
      const pageSize = 500;
      const filters = { dueDateFrom: rangeStart, dueDateTo: rangeEnd };
      const first = await taskService.getUserTasks(userId, 1, pageSize, filters);
      if (!first.hasMore) return first;
      const items = [...first.items];
      let page = 2;
      let hasMore = first.hasMore;
      while (hasMore && page <= 20) {
        const next = await taskService.getUserTasks(userId, page, pageSize, filters);
        items.push(...next.items);
        hasMore = next.hasMore;
        page += 1;
      }
      return { ...first, items, hasMore: false };
    },
    enabled: !!userId,
  });

  const allTasks = tasksResponse?.items ?? [];

  // Get tasks for a specific date (compare the date portion so timezone does not shift the day)
  const getTasksForDate = (date: Dayjs): Task[] => {
    const day = date.format('YYYY-MM-DD');
    const filteredTasks = allTasks.filter((task: Task) => task.dueDate?.slice(0, 10) === day);
    
    // Apply recurring filter if needed
    if (!showRecurring) {
      return filteredTasks.filter((task: Task) => !task.isRecurring);
    }
    
    return filteredTasks;
  };

  // Navigation
  const handlePrev = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.subtract(1, 'month'));
    } else {
      setCurrentDate(currentDate.subtract(1, 'week'));
    }
  };

  const handleNext = () => {
    if (viewMode === 'month') {
      setCurrentDate(currentDate.add(1, 'month'));
    } else {
      setCurrentDate(currentDate.add(1, 'week'));
    }
  };

  const handleToday = () => {
    setCurrentDate(dayjs());
  };

  const handleTaskClick = (taskId: string) => {
    navigate(`/tasks?taskId=${taskId}`);
  };

  // Generate calendar days for month view
  const getMonthDays = () => {
    const startOfMonth = currentDate.startOf('month');
    const daysInMonth = currentDate.daysInMonth();
    const startDay = startOfMonth.day();

    const days: (Dayjs | null)[] = [];

    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(startOfMonth.date(i));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  };

  // Generate week days
  const getWeekDays = () => {
    const startOfWeek = currentDate.startOf('week');
    const days: Dayjs[] = [];

    for (let i = 0; i < 7; i++) {
      days.push(startOfWeek.add(i, 'day'));
    }

    return days;
  };

  // Get priority color
  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.P1:
        return '#ef4444';
      case TaskPriority.P2:
        return '#f59e0b';
      case TaskPriority.P3:
        return '#4F46E5';
      case TaskPriority.P4:
        return '#94a3b8';
      default:
        return '#64748b';
    }
  };

  const weekDayNames = isMobile ? ['S', 'M', 'T', 'W', 'T', 'F', 'S'] : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Calendar for {userName}
        </Typography>
      </Box>

      {/* Calendar Controls */}
      <Card sx={{ mb: 2, borderRadius: { xs: 2, md: 1 } }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: { xs: 1.5, md: 2 },
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: isMobile ? 'wrap' : 'nowrap',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, md: 2 }, flex: isMobile ? '1 1 100%' : 'auto', justifyContent: isMobile ? 'space-between' : 'flex-start' }}>
            <IconButton onClick={handlePrev} size={isMobile ? 'small' : 'medium'}>
              <ChevronLeft size={isMobile ? 18 : 20} />
            </IconButton>
            <Typography variant={isMobile ? 'subtitle1' : 'h6'} fontWeight={600} sx={{ textAlign: 'center', minWidth: { xs: 'auto', md: 200 } }}>
              {viewMode === 'month'
                ? currentDate.format(isMobile ? 'MMM YYYY' : 'MMMM YYYY')
                : `${currentDate.startOf('week').format('MMM D')} - ${currentDate.endOf('week').format(isMobile ? 'D' : 'MMM D, YYYY')}`}
            </Typography>
            <IconButton onClick={handleNext} size={isMobile ? 'small' : 'medium'}>
              <ChevronRight size={isMobile ? 18 : 20} />
            </IconButton>
            {!isMobile && (
              <Button variant="outlined" size="small" onClick={handleToday}>
                Today
              </Button>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 0.5, bgcolor: 'grey.100', borderRadius: 1, p: 0.5 }}>
            <IconButton
              onClick={() => setViewMode('month')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'month' ? 'background.paper' : 'transparent',
                color: viewMode === 'month' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'month' ? 1 : 0,
              }}
            >
              <CalendarDays size={isMobile ? 16 : 18} />
            </IconButton>
            <IconButton
              onClick={() => setViewMode('week')}
              size="small"
              sx={{
                p: { xs: 0.75, md: 1 },
                bgcolor: viewMode === 'week' ? 'background.paper' : 'transparent',
                color: viewMode === 'week' ? 'primary.main' : 'text.secondary',
                boxShadow: viewMode === 'week' ? 1 : 0,
              }}
            >
              <ListIcon size={isMobile ? 16 : 18} />
            </IconButton>
            {isMobile && (
              <Button size="small" onClick={handleToday} sx={{ minWidth: 'auto', px: 1.5, fontSize: '0.75rem' }}>
                Today
              </Button>
            )}
          </Box>

          {/* Recurring Tasks Filter */}
          {!isMobile && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={showRecurring}
                  onChange={(e) => setShowRecurring(e.target.checked)}
                  size="small"
                  icon={<Repeat size={16} style={{ opacity: 0.3 }} />}
                  checkedIcon={<Repeat size={16} />}
                />
              }
              label={<Typography variant="caption">Show recurring</Typography>}
              sx={{ ml: 1 }}
            />
          )}
        </Box>

        {/* Week Day Headers */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            bgcolor: 'grey.50',
          }}
        >
          {weekDayNames.map((day) => (
            <Box
              key={day}
              sx={{
                p: { xs: 0.75, md: 1.5 },
                textAlign: 'center',
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography variant={isMobile ? 'caption' : 'subtitle2'} color="text.secondary" fontWeight={500}>
                {day}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Calendar Grid */}
        {viewMode === 'month' ? (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
            }}
          >
            {getMonthDays().map((day, index) => {
              const cellSx = {
                height: { xs: 56, md: 132 },
                minHeight: { xs: 56, md: 132 },
                maxHeight: { xs: 56, md: 132 },
                overflow: 'hidden',
                borderBottom: '1px solid',
                borderRight: (index + 1) % 7 !== 0 ? '1px solid' : 'none',
                borderColor: 'divider',
              } as const;

              if (!day) {
                return (
                  <Box
                    key={`empty-${index}`}
                    sx={{ ...cellSx, bgcolor: 'grey.50' }}
                  />
                );
              }

              const dayTasks = getTasksForDate(day);
              const isToday = day.isSame(dayjs(), 'day');
              const visibleTasks = dayTasks.slice(0, MONTH_CELL_MAX_ITEMS);
              const hiddenCount = Math.max(0, dayTasks.length - visibleTasks.length);

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  sx={{
                    ...cellSx,
                    p: { xs: 0.5, md: 0.75 },
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: isMobile ? 'center' : 'space-between',
                      alignItems: 'center',
                      mb: { xs: 0, md: 0.5 },
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant={isMobile ? 'caption' : 'body2'}
                      fontWeight={isToday ? 700 : 400}
                      sx={{
                        width: { xs: 24, md: 26 },
                        height: { xs: 24, md: 26 },
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: '50%',
                        bgcolor: isToday ? 'primary.main' : 'transparent',
                        color: isToday ? 'white' : 'text.primary',
                        fontSize: { xs: '0.75rem', md: '0.8125rem' },
                      }}
                    >
                      {day.date()}
                    </Typography>
                    {!isMobile && dayTasks.length > 0 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                        {dayTasks.length}
                      </Typography>
                    )}
                  </Box>

                  {isMobile && dayTasks.length > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.25, mt: 0.25 }}>
                      {dayTasks.slice(0, 3).map((task) => (
                        <Box
                          key={task.id}
                          sx={{
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: getPriorityColor(task.priority),
                          }}
                        />
                      ))}
                      {dayTasks.length > 3 && (
                        <Typography variant="caption" sx={{ fontSize: '0.5rem', lineHeight: 1 }}>
                          +
                        </Typography>
                      )}
                    </Box>
                  )}

                  {!isMobile && (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.35,
                        flex: 1,
                        minHeight: 0,
                        overflow: 'hidden',
                      }}
                    >
                      {visibleTasks.map((task) => {
                        const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                        return (
                          <Tooltip key={task.id} title={task.title}>
                            <Box
                              onClick={() => handleTaskClick(task.id)}
                              sx={{
                                px: 0.5,
                                py: 0.25,
                                borderRadius: 0.5,
                                bgcolor: isBlocked
                                  ? alpha('#f59e0b', 0.2)
                                  : task.isRecurring
                                    ? alpha('#8b5cf6', 0.15)
                                    : `${getPriorityColor(task.priority)}20`,
                                borderLeft: '3px solid',
                                borderColor: isBlocked
                                  ? '#f59e0b'
                                  : task.isRecurring
                                    ? '#8b5cf6'
                                    : getPriorityColor(task.priority),
                                minWidth: 0,
                                cursor: 'pointer',
                                '&:hover': { opacity: 0.85 },
                              }}
                            >
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 500,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                  display: 'block',
                                  fontSize: '0.65rem',
                                  lineHeight: 1.3,
                                  textDecoration:
                                    task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                                }}
                              >
                                {task.title}
                              </Typography>
                            </Box>
                          </Tooltip>
                        );
                      })}
                      {hiddenCount > 0 && (
                        <Typography
                          variant="caption"
                          color="primary.main"
                          onClick={() => setDayList({ date: day, tasks: dayTasks })}
                          sx={{ fontSize: '0.65rem', fontWeight: 600, px: 0.25, cursor: 'pointer' }}
                        >
                          +{hiddenCount} more
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, minmax(0, 1fr))',
              minHeight: { xs: 360, md: 480 },
              maxHeight: { xs: '60vh', md: '65vh' },
            }}
          >
            {getWeekDays().map((day, index) => {
              const dayTasks = getTasksForDate(day);
              const isToday = day.isSame(dayjs(), 'day');
              const visibleTasks = dayTasks.slice(0, WEEK_VISIBLE_TASKS);
              const hiddenTasks = dayTasks.length - visibleTasks.length;

              return (
                <Box
                  key={day.format('YYYY-MM-DD')}
                  sx={{
                    minHeight: 0,
                    maxHeight: '100%',
                    p: 1,
                    borderRight: index < 6 ? '1px solid' : 'none',
                    borderColor: 'divider',
                    bgcolor: isToday ? 'primary.50' : 'transparent',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                  }}
                >
                  <Box sx={{ textAlign: 'center', mb: 1, flexShrink: 0 }}>
                    <Typography
                      variant="h5"
                      fontWeight={isToday ? 700 : 500}
                      sx={{ color: isToday ? 'primary.main' : 'text.primary', lineHeight: 1.2 }}
                    >
                      {day.date()}
                    </Typography>
                    {dayTasks.length > 0 && (
                      <Typography variant="caption" color="text.secondary">
                        {dayTasks.length} tasks
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      overflowX: 'hidden',
                    }}
                  >
                    {visibleTasks.map((task) => {
                      const isBlocked = (task.blockedByTaskIds?.length || 0) > 0;
                      return (
                        <Box
                          key={task.id}
                          onClick={() => handleTaskClick(task.id)}
                          sx={{
                            p: 1,
                            borderRadius: 1,
                            borderLeft: '3px solid',
                            borderColor: isBlocked
                              ? '#f59e0b'
                              : task.isRecurring
                                ? '#8b5cf6'
                                : getPriorityColor(task.priority),
                            bgcolor: isBlocked
                              ? alpha('#f59e0b', 0.1)
                              : task.isRecurring
                                ? alpha('#8b5cf6', 0.08)
                                : 'background.paper',
                            border: '1px solid',
                            borderTopColor: 'divider',
                            borderRightColor: 'divider',
                            borderBottomColor: 'divider',
                            cursor: 'pointer',
                            flexShrink: 0,
                            minWidth: 0,
                            '&:hover': { boxShadow: 1 },
                          }}
                        >
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            noWrap
                            display="block"
                            sx={{
                              textDecoration:
                                task.status === TaskStatus.COMPLETED ? 'line-through' : 'none',
                            }}
                          >
                            {task.title}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {task.priority}
                          </Typography>
                        </Box>
                      );
                    })}
                    {hiddenTasks > 0 && (
                      <Typography
                        variant="caption"
                        color="primary.main"
                        fontWeight={600}
                        onClick={() => setDayList({ date: day, tasks: dayTasks })}
                        sx={{ textAlign: 'center', py: 0.5, cursor: 'pointer' }}
                      >
                        +{hiddenTasks} more tasks
                      </Typography>
                    )}
                    {dayTasks.length === 0 && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textAlign: 'center', py: 2 }}
                      >
                        No tasks
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Card>

      <Dialog open={!!dayList} onClose={() => setDayList(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {dayList ? `${userName} · ${dayList.date.format('ddd, MMM D')}` : ''}
        </DialogTitle>
        <DialogContent dividers>
          <List dense disablePadding>
            {(dayList?.tasks ?? []).map((task) => (
              <ListItemButton key={task.id} onClick={() => handleTaskClick(task.id)}>
                <ListItemText
                  primary={task.title}
                  secondary={task.priority}
                  primaryTypographyProps={{
                    noWrap: true,
                    sx: { textDecoration: task.status === TaskStatus.COMPLETED ? 'line-through' : 'none' },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default UserCalendarView;
